/* eslint-disable @altermaind/no-magic-numbers */
import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { Tooltip } from '@/_shared/components/Tooltip';
import { Routes } from '@/_shared/constants/routes';
import { cutText } from '@/_shared/helpers';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getDepartmentsCall } from '@/departments/state';

type Props = {
  total?: number;
  completed?: number;
  invalid?: number;
  noTitle?: boolean;
};

export const OverviewEmployees: FC<Props> = ({ total, completed, invalid, noTitle }) => {
  const t = useTranslations('dashboard.overview');
  const { genericResponsiveValue } = useResponsiveStyle();

  const gapS = useThemeValue('space', 's');
  const navigate = useNavigate();

  const { execute: executeGetDepartments, data, totalItems } = getDepartmentsCall.hook();

  const goToEmployees = () => {
    navigate(Routes.People);
  };

  const goToReview = () => {
    navigate(Routes.PeopleReview);
  };

  const goToDepartments = () => {
    navigate(Routes.Departments);
  };

  useEffect(() => {
    executeGetDepartments({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box mt='m'>
      {!noTitle && <Typography variant='h2'>{t('title')}</Typography>}
      <Box
        mt='s'
        display='flex'
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        style={{ gap: gapS }}
      >
        <Card flex='1'>
          <Box display='flex' flexDirection='column'>
            <Box display='flex' alignItems='center'>
              <Typography variant='h2' mt='none'>
                {t('current.title')}
              </Typography>
              <Tooltip
                iconProps={{ size: 'xs', fill: 'grey/light' }}
                containerProps={{ ml: 'xxs' }}
              >
                {t('current.tooltip')}
              </Tooltip>
            </Box>
            <Typography data-testid='overview-employees-total' variant='h1' mt='xxxs'>
              {total}
            </Typography>
          </Box>
          {invalid && invalid > 0 ? (
            <>
              <Box
                mt='xs'
                display='flex'
                flexDirection={genericResponsiveValue({
                  breakPoints: { desktop: 'row' },
                  defaultValue: 'column',
                })}
                alignItems={genericResponsiveValue({
                  breakPoints: { desktop: 'center' },
                  defaultValue: 'flex-start',
                })}
              >
                <Box flex='1'>
                  <Box display='flex' alignItems='center'>
                    <Typography variant='h4' mt='none'>
                      {t('validProfile.title')}
                    </Typography>
                    <Tooltip
                      iconProps={{ size: 'xs', fill: 'grey/light' }}
                      containerProps={{ ml: 'xxs' }}
                    >
                      {t('validProfile.tooltip')}
                    </Tooltip>
                  </Box>
                  <Typography variant='p1' mt='xxxs'>
                    {completed}
                  </Typography>
                </Box>
                <Box flex='1'>
                  <Box display='flex' alignItems='center'>
                    <Typography variant='h4' mt='none'>
                      {t('invalidProfile.title')}
                    </Typography>
                    <Tooltip
                      iconProps={{ size: 'xs', fill: 'grey/light' }}
                      containerProps={{ ml: 'xxs' }}
                    >
                      {t('invalidProfile.tooltip')}
                    </Tooltip>
                  </Box>
                  <Typography
                    data-testid='overview-employees-invalid'
                    color='error'
                    variant='p1'
                    mt='xxxs'
                  >
                    {invalid}
                  </Typography>
                </Box>
              </Box>
              <Box mt='xs' display='flex' flexDirection='row' alignItems='center'>
                <Box flex='1' display='flex' justifyContent='flex-start' onClick={goToReview}>
                  <Button variant='positive'>{t('cta.fixProfiles')}</Button>
                </Box>
              </Box>
            </>
          ) : (
            <>
              <Box mt='s'>
                <Typography variant='p2/light'>{t('noInvalid.text1')}</Typography>
                <Typography variant='p2/light'>{t('noInvalid.text2')}</Typography>
              </Box>
              <Box mt='xs' display='flex' flexDirection='row' alignItems='center'>
                <Box flex='1' display='flex' justifyContent='flex-start' onClick={goToEmployees}>
                  <Button width='fit-content' variant='positive'>
                    {t('cta.goToPeople')}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Card>
        {data && data.length > 0 && (
          <Card flex='1' display='flex' flexDirection='column' justifyContent='space-between'>
            <Box
              alignItems='center'
              display='flex'
              flexDirection='row'
              justifyContent='space-between'
              mt='xxxs'
              pb='s'
            >
              <Typography variant='h3'>{t('departments.title')}</Typography>
              <Box alignItems='center' flexDirection='row' display='flex'>
                <Svg name='team' size='s' />
                <Typography ml='xxs' variant='h2'>
                  {totalItems}
                </Typography>
              </Box>
            </Box>
            <Box display='flex' flexDirection='row' flexWrap='wrap'>
              {data && data.length > 0
                ? data.slice(0, 6).map(department => (
                    <Card
                      key={department.id}
                      backgroundColor='primary'
                      mb='xxxs'
                      px='xxs'
                      py='xxxxs'
                      width='fit-content'
                      mr='xxs'
                    >
                      <Typography variant='p2/light' fontSize='xs'>
                        {department.name !== 'unknown'
                          ? cutText(department.name || '', 10)
                          : t('departments.noDepartmentName')}{' '}
                        ({department.totalEmployees})
                      </Typography>
                    </Card>
                  ))
                : null}
              {data && data.length > 6 ? (
                <Card
                  backgroundColor='primary'
                  mb='xxxs'
                  px='xxs'
                  py='xxxxs'
                  width='fit-content'
                  mr='xxs'
                >
                  <Typography variant='p2/light' fontSize='xs'>
                    +{data.length - 6}
                  </Typography>
                </Card>
              ) : null}
            </Box>
            <Button width='fit-content' onClick={goToDepartments} variant='positive' mt='m'>
              {t('departments.cta')}
            </Button>
          </Card>
        )}
      </Box>
    </Box>
  );
};
